import React, { useState, useEffect } from 'react'
import { format, subDays, addDays, isSameDay } from 'date-fns'
import { Button } from "../components/ui/button"
import { Textarea } from "../components/ui/textarea"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card"
import { ScrollArea } from "../components/ui/scroll-area"
import { Badge } from "../components/ui/badge"
import { ChevronLeft, ChevronRight, Image, Award, Mic } from 'lucide-react'

const moods = ['😢', '😕', '😐', '🙂', '😄']

const quotes = [
  "The only way to do great work is to love what you do. - Steve Jobs",
  "Be yourself; everyone else is already taken. - Oscar Wilde",
  "You are never too old to set another goal or to dream a new dream. - C.S. Lewis",
  "The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt",
  "Don't watch the clock; do what it does. Keep going. - Sam Levenson"
]

const mockEntries = [
  { date: new Date(2023, 5, 1), content: "Started my new job today. Feeling excited and nervous!", mood: 3 },
  { date: new Date(2023, 5, 3), content: "Had a great team meeting. Projects are looking promising.", mood: 4 },
  { date: new Date(2023, 5, 5), content: "Struggled with a difficult task at work. Need to improve my skills.", mood: 1 },
  { date: new Date(2023, 5, 7), content: "Weekend getaway with friends. Much needed break!", mood: 4 },
  { date: new Date(2023, 5, 10), content: "Received positive feedback from my manager. Feeling motivated!", mood: 4 },
]

export default function JournalPage() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [entry, setEntry] = useState('')
  const [mood, setMood] = useState(2)
  const [streak, setStreak] = useState(5)
  const [entries, setEntries] = useState(mockEntries)
  const [isRecording, setIsRecording] = useState(false)

  const formattedDate = format(currentDate, 'EEEE, MMMM d, yyyy')
  const quote = quotes[currentDate.getDate() % quotes.length]

  const handlePrevDay = () => setCurrentDate(subDays(currentDate, 1))
  const handleNextDay = () => setCurrentDate(addDays(currentDate, 1))

  const handleSave = () => {
    const newEntry = { date: currentDate, content: entry, mood }
    setEntries([...entries, newEntry])
    setStreak(streak + 1)
  }

  const selectEntry = (date: Date) => {
    setCurrentDate(date)
    const selectedEntry = entries.find(e => isSameDay(e.date, date))
    if (selectedEntry) {
      setEntry(selectedEntry.content)
      setMood(selectedEntry.mood)
    } else {
      setEntry('')
      setMood(2)
    }
  }

  const handleVoiceRecording = () => {
    setIsRecording(!isRecording)
    if (!isRecording) {
      setTimeout(() => {
        setEntry(prev => prev + " [Voice recorded content would appear here]")
        setIsRecording(false)
      }, 3000)
    }
  }

  useEffect(() => {
    const currentEntry = entries.find(e => isSameDay(e.date, currentDate))
    if (currentEntry) {
      setEntry(currentEntry.content)
      setMood(currentEntry.mood)
    } else {
      setEntry('')
      setMood(2)
    }
  }, [currentDate, entries])

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-100">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Journal Entries</h2>
          <ScrollArea className="h-[calc(100vh-8rem)]">
            {entries.map((entry, index) => (
              <div
                key={index}
                className="mb-3 cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => selectEntry(entry.date)}
              >
                <div className="font-medium text-gray-700">{format(entry.date, 'MMM d, yyyy')}</div>
                <div className="text-sm text-gray-500 truncate">{entry.content}</div>
              </div>
            ))}
          </ScrollArea>
        </div>
      </div>

      {/* Journal Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <Card className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
          <CardHeader className="bg-[#52A384] text-white rounded-t-lg p-6">
            <CardTitle className="text-2xl font-bold">My Journal</CardTitle>
            <p className="text-green-100 mt-1">{formattedDate}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" onClick={handlePrevDay} className="text-[#52A384] hover:bg-green-50">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              <Button variant="outline" onClick={handleNextDay} className="text-[#52A384] hover:bg-green-50">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
            <div className="mb-4 italic text-gray-600 text-center bg-green-50 p-3 rounded">&ldquo;{quote}&rdquo;</div>
            <div className="relative mb-4">
              <Textarea
                placeholder="Write your thoughts for today..."
                className="min-h-[200px] p-4 rounded border-gray-300 focus:border-green-500 focus:ring focus:ring-green-200"
                value={entry}
                onChange={(e) => setEntry(e.target.value)}
              />
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-2 bottom-2 ${isRecording ? 'text-red-500' : 'text-green-500'}`}
                onClick={handleVoiceRecording}
              >
                <Mic className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="font-semibold">Mood:</span>
                <div className="flex space-x-1">
                  {moods.map((moodEmoji, index) => (
                    <button
                      key={index}
                      className={`text-2xl ${index === mood ? 'opacity-100 scale-125' : 'opacity-50 hover:opacity-75'} transition-all duration-200`}
                      onClick={() => setMood(index)}
                    >
                      {moodEmoji}
                    </button>
                  ))}
                </div>
              </div>
              <Badge variant="secondary" className="text-sm bg-green-100 text-green-800">
                <Award className="h-4 w-4 mr-1" />
                {streak} day streak!
              </Badge>
            </div>
          </CardContent>
          <CardFooter className="bg-gray-50 rounded-b-lg p-4">
            <Button variant="outline" className="flex-1 mr-2 text-[#52A384] hover:bg-green-50">
              <Image className="h-4 w-4 mr-2" />
              Add Photo
            </Button>
            <Button className="flex-1 ml-2 bg-[#52A384] hover:bg-green-600 text-white" onClick={handleSave}>
              Save Entry
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}